import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { DashboardLayout } from "@/pages/DashboardPage";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  User, Buildings, Globe, WhatsappLogo, Briefcase, Info,
  PencilSimple, X, Check, Camera, ShareNetwork,
  Phone, Plus, Trash, Package, CurrencyInr,
  MapPin, EnvelopeSimple, LinkSimple, ArrowUpRight,
  Timer, ShieldCheck, CalendarBlank, Image as ImageIcon,
  Medal, Certificate, FilePdf, FileImage, ChatTeardropText, Quotes,
  CaretLeft, CaretRight
} from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";
import { LoadingImage } from "@/components/LoadingImage";

const LOGO_URL = "/bch-logo.png";

function getMemberAge(createdAt) {
  if (!createdAt) return null;
  const created = new Date(createdAt);
  const now = new Date();
  const days = Math.floor((now - created) / (1000 * 60 * 60 * 24));
  if (days < 30) return "New Member";
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? "1 month old member" : `${months} months old member`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (rem === 0) return years === 1 ? "1 year old member" : `${years} years old member`;
  return `${years}y ${rem}m old member`;
}

export default function PublicProfilePage() {
  const { userId } = useParams();
  const { token, user: currentUser, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [catalogue, setCatalogue] = useState([]);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [awards, setAwards] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [showAddAward, setShowAddAward] = useState(false);
  const [showAddCert, setShowAddCert] = useState(false);
  const [awardForm, setAwardForm] = useState({ title: "", description: "" });
  const [certForm, setCertForm] = useState({ title: "", file_url: "" });
  const [certUploading, setCertUploading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profileTestimonials, setProfileTestimonials] = useState([]);

  const isOwner = currentUser?.id === userId;
  const isLoggedIn = isAuthenticated;
  const profilePicRef = useRef(null);
  const coverPicRef = useRef(null);

  useEffect(() => { fetchProfile(); fetchCatalogue(); fetchTestimonials(); }, [userId]);

  useEffect(() => {
    if (isOwner && token) {
      axios.get(`${API}/membership/status`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setMembershipStatus(res.data))
        .catch(() => {});
    }
  }, [isOwner, token]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/public/profile/${userId}`);
      setProfile(res.data);
      setAwards(res.data.awards || []);
      setCertifications(res.data.certifications || []);
      setEditForm({
        business_name: res.data.business_name || "",
        about_user: res.data.about_user || "",
        about_company: res.data.about_company || "",
        website_link: res.data.website_link || "",
        whatsapp_number: res.data.whatsapp_number || "",
        company_logo: res.data.company_logo || "",
        profile_picture: res.data.profile_picture || "",
        cover_picture: res.data.cover_picture || "",
        product_catalogue: res.data.product_catalogue || "",
        full_name: res.data.full_name || "",
        phone: res.data.phone || "",
      });
    } catch { setProfile(null); }
    finally { setLoading(false); }
  };

  const fetchCatalogue = async () => {
    try {
      const res = await axios.get(`${API}/catalogue/${userId}`);
      setCatalogue(res.data || []);
    } catch { setCatalogue([]); }
  };

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API}/public/testimonials/${userId}`);
      setProfileTestimonials(res.data || []);
    } catch { setProfileTestimonials([]); }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      return res.data.url;
    } catch { toast.error("Upload failed"); return null; }
    finally { setUploading(false); }
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) {
      setEditForm(p => ({ ...p, profile_picture: url }));
      toast.success("Photo uploaded!");
    }
  };

  const handleCoverPicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) {
      setEditForm(p => ({ ...p, cover_picture: url }));
      toast.success("Cover photo uploaded!");
    }
  };

  const handleAddAward = async () => {
    if (!awardForm.title.trim()) return toast.error("Award title is required");
    const newAward = { id: Date.now().toString(), ...awardForm };
    const updated = [...awards, newAward];
    try {
      await axios.put(`${API}/profile/update`, { awards: updated }, { headers: { Authorization: `Bearer ${token}` } });
      setAwards(updated);
      setAwardForm({ title: "", description: "" });
      setShowAddAward(false);
      toast.success("Award added!");
    } catch { toast.error("Failed to add award"); }
  };

  const handleDeleteAward = async (id) => {
    const updated = awards.filter(a => a.id !== id);
    try {
      await axios.put(`${API}/profile/update`, { awards: updated }, { headers: { Authorization: `Bearer ${token}` } });
      setAwards(updated);
      toast.success("Award removed");
    } catch { toast.error("Failed to remove award"); }
  };

  const handleCertUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) return toast.error("Only JPEG, PNG, WebP or PDF files allowed");
    setCertUploading(true);
    const url = await uploadImage(file);
    setCertUploading(false);
    if (url) setCertForm(p => ({ ...p, file_url: url }));
  };

  const handleAddCert = async () => {
    if (!certForm.title.trim() || !certForm.file_url) return toast.error("Title and file are required");
    const newCert = { id: Date.now().toString(), ...certForm };
    const updated = [...certifications, newCert];
    try {
      await axios.put(`${API}/profile/update`, { certifications: updated }, { headers: { Authorization: `Bearer ${token}` } });
      setCertifications(updated);
      setCertForm({ title: "", file_url: "" });
      setShowAddCert(false);
      toast.success("Certification added!");
    } catch { toast.error("Failed to add certification"); }
  };

  const handleDeleteCert = async (id) => {
    const updated = certifications.filter(c => c.id !== id);
    try {
      await axios.put(`${API}/profile/update`, { certifications: updated }, { headers: { Authorization: `Bearer ${token}` } });
      setCertifications(updated);
      toast.success("Certification removed");
    } catch { toast.error("Failed to remove certification"); }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/profile/update`, editForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Profile updated!");
      setEditing(false);
      fetchProfile();
    } catch { toast.error("Failed to update profile"); }
  };

  const handleShare = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("Profile link copied!"); } catch {}
  };

  if (loading) {
    const spinner = (
      <div className="flex items-center justify-center py-32">
        <div className="w-10 h-10 border-3 border-[#2A9D8F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
    return isLoggedIn ? <DashboardLayout>{spinner}</DashboardLayout> : <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">{spinner}</div>;
  }

  if (!profile) {
    const notFound = (
      <div className="text-center py-32">
        <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
          <User className="w-10 h-10 text-slate-300" weight="duotone" />
        </div>
        <p className="text-slate-900 font-bold text-xl tracking-tight">Profile Not Found</p>
        <p className="text-slate-500 text-sm mt-2">This profile may have been removed or doesn't exist.</p>
      </div>
    );
    return isLoggedIn ? <DashboardLayout>{notFound}</DashboardLayout> : <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-8">{notFound}</div>;
  }

  const initials = profile.full_name?.split(" ").map(n => n[0]).join("").slice(0, 2) || "?";

  const profileContent = (
    <div data-testid="public-profile-page" className="pb-16">
      <SEO
        title={profile.full_name ? `${profile.full_name} - ${profile.business_category || "Member"}` : "Member Profile"}
        description={profile.about_user || `${profile.full_name || "Member"} at ${profile.business_name || "BCH"}. ${profile.business_category || ""} - ${profile.chapter_name || "BCH Chapter"}. Connect with verified business owners on BCH Networking.`}
        path={`/p/${userId}`}
        ogImage={profile.profile_picture || undefined}
        jsonLd={profile.full_name ? {
          "@context": "https://schema.org",
          "@type": "Person",
          "name": profile.full_name,
          "jobTitle": profile.business_category || undefined,
          "worksFor": profile.business_name ? { "@type": "Organization", "name": profile.business_name } : undefined,
          "description": profile.about_user || undefined,
          "image": profile.profile_picture || undefined,
          "memberOf": { "@type": "Organization", "name": "BCH Networking" }
        } : undefined}
      />
      {/* ===== HERO BANNER ===== */}
      <div className="relative">
        <div className="h-48 sm:h-56 rounded-3xl overflow-hidden relative" style={{
          background: (editing ? editForm.cover_picture : profile.cover_picture) 
            ? "none" 
            : "linear-gradient(135deg, rgba(42,157,143,0.12) 0%, rgba(233,196,106,0.15) 50%, rgba(42,157,143,0.08) 100%)"
        }}>
          {(editing ? editForm.cover_picture : profile.cover_picture) && (
            <LoadingImage src={editing ? editForm.cover_picture : profile.cover_picture} className="absolute inset-0 w-full h-full" />
          )}
          {!(editing ? editForm.cover_picture : profile.cover_picture) && (
            <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="1" fill="#0F172A" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#dots)" />
            </svg>
          )}
          {isOwner && editing && (
            <>
              <button
                onClick={() => coverPicRef.current?.click()}
                className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-2 bg-black/50 backdrop-blur-sm text-white rounded-xl text-xs font-semibold hover:bg-black/70 transition-all duration-300"
                data-testid="upload-cover-pic-btn"
              >
                <ImageIcon className="w-4 h-4" weight="fill" /> {editForm.cover_picture ? "Change Cover" : "Add Cover Photo"} <span className="text-white/60 ml-1">(1920 x 480 px)</span>
              </button>
              <input ref={coverPicRef} type="file" accept="image/*" className="hidden" onChange={handleCoverPicUpload} data-testid="cover-pic-input" />
            </>
          )}
        </div>

        {/* Avatar overlapping the banner */}
        <div className="absolute -bottom-16 left-8 sm:left-10">
          <div className="relative group">
            <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-3xl bg-gradient-to-br from-[#2A9D8F] to-[#1F7A6F] flex items-center justify-center overflow-hidden border-[5px] border-white shadow-xl shadow-slate-200/60">
              {(editing ? editForm.profile_picture : profile.profile_picture) ? (
                <LoadingImage src={editing ? editForm.profile_picture : profile.profile_picture} className="w-full h-full rounded-3xl" />
              ) : (
                <span className="text-white text-4xl sm:text-5xl font-bold tracking-tight">{initials}</span>
              )}
            </div>
            {isOwner && editing && (
              <>
                <button
                  onClick={() => profilePicRef.current?.click()}
                  className="absolute inset-0 rounded-3xl bg-black/40 flex flex-col items-center justify-center cursor-pointer transition-opacity duration-300"
                  data-testid="upload-profile-pic-btn"
                >
                  <Camera className="w-8 h-8 text-white" weight="fill" />
                  <span className="text-white text-[10px] font-semibold mt-1">{uploading ? "Uploading..." : "Change Photo"}</span>
                  <span className="text-white/60 text-[9px] mt-0.5">400 x 400 px</span>
                </button>
                <input ref={profilePicRef} type="file" accept="image/*" className="hidden" onChange={handleProfilePicUpload} data-testid="profile-pic-input" />
              </>
            )}
          </div>
        </div>

        {/* Top-right action buttons */}
        <div className="absolute top-5 right-5 flex items-center gap-2.5">
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-4 py-2.5 bg-white/80 backdrop-blur-sm border border-slate-200/60 rounded-xl text-slate-700 text-xs font-medium hover:-translate-y-0.5 hover:shadow-md transition-all duration-300"
            data-testid="share-profile-btn"
          >
            <ShareNetwork className="w-4 h-4" weight="bold" /> Share
          </button>
          {isOwner && (
            <button
              onClick={() => setEditing(!editing)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold hover:-translate-y-0.5 hover:shadow-md transition-all duration-300 ${
                editing
                  ? "bg-red-50 text-red-600 border border-red-200"
                  : "bg-[#2A9D8F] text-white shadow-lg shadow-[#2A9D8F]/20"
              }`}
              data-testid="edit-profile-btn"
            >
              {editing ? <><X className="w-4 h-4" weight="bold" /> Cancel</> : <><PencilSimple className="w-4 h-4" weight="bold" /> Edit Profile</>}
            </button>
          )}
        </div>
      </div>

      {/* ===== PROFILE HEADER INFO (below avatar) ===== */}
      <div className="mt-20 sm:mt-20 px-2">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="profile-name">
              {profile.full_name}
            </h1>
            <div className="flex flex-wrap items-center gap-2.5 mt-3">
              {profile.business_category && (
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#2A9D8F]/8 text-[#1F7A6F] rounded-full text-xs font-semibold tracking-wide">
                  <Briefcase className="w-3.5 h-3.5" weight="duotone" />
                  {profile.business_category}
                </span>
              )}
              {profile.chapter_name && (
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#E9C46A]/15 text-[#9A7B2A] rounded-full text-xs font-semibold tracking-wide">
                  <MapPin className="w-3.5 h-3.5" weight="duotone" />
                  {profile.chapter_name}
                </span>
              )}
              {profile.created_at && (
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-sky-500/10 text-sky-600 rounded-full text-xs font-semibold tracking-wide" data-testid="member-since-badge">
                  <Timer className="w-3.5 h-3.5" weight="fill" />
                  {getMemberAge(profile.created_at)}
                </span>
              )}
            </div>
          </div>

          {/* Contact Actions */}
          {!editing && (
            <div className="flex items-center gap-2.5">
              {profile.website_link && (
                <a href={profile.website_link} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
                  data-testid="contact-website-btn"
                >
                  <Globe className="w-4 h-4" weight="bold" /> Website <ArrowUpRight className="w-3 h-3" weight="bold" />
                </a>
              )}
              {profile.whatsapp_number && (
                <a href={`https://wa.me/${profile.whatsapp_number.replace(/[^0-9]/g,"")}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#25D366] text-white rounded-xl text-xs font-semibold hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#25D366]/20 transition-all duration-300"
                  data-testid="contact-whatsapp-btn"
                >
                  <WhatsappLogo className="w-4 h-4" weight="fill" /> WhatsApp
                </a>
              )}
              {profile.phone && (
                <a href={`tel:${profile.phone}`}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-semibold hover:-translate-y-0.5 hover:shadow-md transition-all duration-300"
                  data-testid="contact-call-btn"
                >
                  <Phone className="w-4 h-4" weight="duotone" /> Call
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ===== EDIT FORM ===== */}
      {editing && (
        <div className="mt-8 bg-white border border-slate-200/60 rounded-3xl p-6 sm:p-8 shadow-sm" data-testid="edit-form">
          <h3 className="text-slate-900 font-bold text-lg tracking-tight mb-6 flex items-center gap-2">
            <PencilSimple className="w-5 h-5 text-[#2A9D8F]" weight="duotone" /> Edit Profile
          </h3>
          {uploading && (
            <div className="text-[#2A9D8F] text-sm font-medium flex items-center gap-2 mb-4">
              <div className="w-4 h-4 border-2 border-[#2A9D8F] border-t-transparent rounded-full animate-spin" /> Uploading photo...
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <EditField label="Full Name" value={editForm.full_name} onChange={v => setEditForm(p => ({ ...p, full_name: v }))} testId="edit-full-name" />
            <EditField label="Phone" value={editForm.phone} onChange={v => setEditForm(p => ({ ...p, phone: v.replace(/\D/g, "").slice(0, 10) }))} testId="edit-phone" maxLength={10} inputMode="numeric" />
            <EditField label="Business Name" value={editForm.business_name} onChange={v => setEditForm(p => ({ ...p, business_name: v }))} testId="edit-business-name" />
            <EditField label="Website Link" value={editForm.website_link} onChange={v => setEditForm(p => ({ ...p, website_link: v }))} testId="edit-website" />
            <EditField label="WhatsApp Number" value={editForm.whatsapp_number} onChange={v => setEditForm(p => ({ ...p, whatsapp_number: v.replace(/\D/g, "").slice(0, 10) }))} testId="edit-whatsapp" maxLength={10} inputMode="numeric" />
          </div>
          <div className="grid grid-cols-1 gap-5 mt-5">
            <EditField label="About You" value={editForm.about_user} onChange={v => setEditForm(p => ({ ...p, about_user: v }))} multiline testId="edit-about-user" maxLength={1000} />
            <EditField label="About Company" value={editForm.about_company} onChange={v => setEditForm(p => ({ ...p, about_company: v }))} multiline testId="edit-about-company" maxLength={1000} />
          </div>
          <div className="flex gap-3 pt-6 border-t border-slate-100 mt-6">
            <button onClick={handleSave} className="flex items-center gap-2 px-6 py-3 bg-[#2A9D8F] text-white rounded-xl font-semibold text-sm hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#2A9D8F]/20 transition-all duration-300" data-testid="save-profile-btn">
              <Check className="w-4 h-4" weight="bold" /> Save Changes
            </button>
            <button onClick={() => setEditing(false)} className="px-6 py-3 bg-slate-50 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-100 transition-all duration-300">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ===== BENTO GRID CONTENT ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-10 lg:items-stretch">
        {/* LEFT COLUMN — About */}
        <div className="lg:col-span-8">
          {(profile.about_user || profile.about_company) && (
            <div className="bg-white border border-slate-200/60 rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow duration-300 h-full flex flex-col">
              <h2 className="text-slate-900 font-bold text-lg tracking-tight mb-5 flex items-center gap-2.5 shrink-0">
                <div className="w-8 h-8 rounded-lg bg-[#2A9D8F]/10 flex items-center justify-center">
                  <Info className="w-4.5 h-4.5 text-[#2A9D8F]" weight="duotone" />
                </div>
                About
              </h2>
              <div className="flex-1 overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
              {profile.about_user && (
                <div className="mb-5">
                  <p className="text-slate-400 text-[11px] uppercase tracking-widest font-bold mb-2">About Me</p>
                  <p className="text-slate-600 text-sm leading-relaxed" data-testid="about-user-text">{profile.about_user}</p>
                </div>
              )}
              {profile.about_company && (
                <div>
                  <p className="text-slate-400 text-[11px] uppercase tracking-widest font-bold mb-2">About the Company</p>
                  <p className="text-slate-600 text-sm leading-relaxed" data-testid="about-company-text">{profile.about_company}</p>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Company Logo — show below grid if present */}
        </div>

        {/* RIGHT COLUMN — Awards & Certifications */}
        <div className="lg:col-span-4 flex flex-col gap-6">
          {(awards.length > 0 || isOwner) && (
            <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm flex-1" data-testid="awards-section">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-slate-900 font-bold text-lg tracking-tight flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Medal className="w-4.5 h-4.5 text-amber-500" weight="fill" />
                  </div>
                  Awards & Achievements
                </h2>
                {isOwner && (
                  <button onClick={() => setShowAddAward(!showAddAward)}
                    className="flex items-center gap-1.5 text-[#2A9D8F] text-sm font-semibold hover:text-[#238076] transition-colors"
                    data-testid="add-award-btn">
                    <Plus className="w-4 h-4" weight="bold" /> Add
                  </button>
                )}
              </div>

              {showAddAward && isOwner && (
                <div className="mb-5 bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="add-award-form">
                  <Input value={awardForm.title} onChange={e => setAwardForm(p => ({...p, title: e.target.value}))}
                    placeholder="Award title" className="bg-white border-slate-200 text-slate-900 rounded-xl" data-testid="award-title-input" />
                  <textarea value={awardForm.description} onChange={e => setAwardForm(p => ({...p, description: e.target.value}))}
                    placeholder="Description (optional)" rows={2}
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 resize-none focus:outline-none focus:border-[#2A9D8F]" data-testid="award-desc-input" />
                  <div className="flex gap-2">
                    <Button onClick={handleAddAward} className="bg-[#2A9D8F] hover:bg-[#238076] text-white text-sm" size="sm" data-testid="save-award-btn">Save</Button>
                    <Button onClick={() => { setShowAddAward(false); setAwardForm({ title: "", description: "" }); }} variant="ghost" size="sm" className="text-slate-500">Cancel</Button>
                  </div>
                </div>
              )}

              {awards.length === 0 && !showAddAward ? (
                <p className="text-slate-400 text-sm text-center py-4">No awards added yet</p>
              ) : (
                <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
                  {awards.map(award => (
                    <div key={award.id} className="flex items-start gap-3 bg-amber-50/50 border border-amber-100 rounded-2xl p-4" data-testid={`award-${award.id}`}>
                      <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0 mt-0.5">
                        <Medal className="w-5 h-5 text-amber-500" weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-slate-800 font-semibold text-sm">{award.title}</h4>
                        {award.description && <p className="text-slate-500 text-xs mt-0.5 leading-relaxed">{award.description}</p>}
                      </div>
                      {isOwner && (
                        <button onClick={() => handleDeleteAward(award.id)} className="text-red-300 hover:text-red-500 transition-colors shrink-0 p-1" data-testid={`delete-award-${award.id}`}>
                          <Trash className="w-4 h-4" weight="bold" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== CERTIFICATIONS SECTION ===== */}
          {(certifications.length > 0 || isOwner) && (
            <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm flex-1" data-testid="certifications-section">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-slate-900 font-bold text-lg tracking-tight flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Certificate className="w-4.5 h-4.5 text-blue-500" weight="fill" />
                  </div>
                  Certifications
                </h2>
                {isOwner && (
                  <button onClick={() => setShowAddCert(!showAddCert)}
                    className="flex items-center gap-1.5 text-[#2A9D8F] text-sm font-semibold hover:text-[#238076] transition-colors"
                    data-testid="add-cert-btn">
                    <Plus className="w-4 h-4" weight="bold" /> Add
                  </button>
                )}
              </div>

              {showAddCert && isOwner && (
                <div className="mb-5 bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="add-cert-form">
                  <Input value={certForm.title} onChange={e => setCertForm(p => ({...p, title: e.target.value}))}
                    placeholder="Certification title" className="bg-white border-slate-200 text-slate-900 rounded-xl" data-testid="cert-title-input" />
                  <div>
                    <label className="flex items-center gap-2 px-4 py-3 bg-white border border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#2A9D8F] transition-colors">
                      <Certificate className="w-5 h-5 text-slate-400" />
                      <span className="text-slate-500 text-sm">{certUploading ? "Uploading..." : certForm.file_url ? "File uploaded — click to change" : "Upload certificate (JPEG, PNG, PDF)"}</span>
                      <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={handleCertUpload} data-testid="cert-file-input" />
                    </label>
                    {certForm.file_url && (
                      <p className="text-xs text-emerald-500 mt-1 flex items-center gap-1"><Check className="w-3 h-3" weight="bold" /> File ready</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddCert} disabled={certUploading} className="bg-[#2A9D8F] hover:bg-[#238076] text-white text-sm" size="sm" data-testid="save-cert-btn">Save</Button>
                    <Button onClick={() => { setShowAddCert(false); setCertForm({ title: "", file_url: "" }); }} variant="ghost" size="sm" className="text-slate-500">Cancel</Button>
                  </div>
                </div>
              )}

              {certifications.length === 0 && !showAddCert ? (
                <p className="text-slate-400 text-sm text-center py-4">No certifications added yet</p>
              ) : (
                <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
                  {certifications.map(cert => (
                    <div key={cert.id} className="flex items-start gap-3 bg-blue-50/50 border border-blue-100 rounded-2xl p-4 group" data-testid={`cert-${cert.id}`}>
                      <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center shrink-0 mt-0.5">
                        {cert.file_url?.endsWith(".pdf") ? (
                          <FilePdf className="w-5 h-5 text-red-500" weight="fill" />
                        ) : (
                          <FileImage className="w-5 h-5 text-blue-500" weight="fill" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-slate-800 font-semibold text-sm">{cert.title}</h4>
                        <a href={cert.file_url} target="_blank" rel="noopener noreferrer"
                          className="text-blue-500 text-xs hover:underline flex items-center gap-1 mt-0.5">
                          <ArrowUpRight className="w-3 h-3" /> View Certificate
                        </a>
                      </div>
                      {isOwner && (
                        <button onClick={() => handleDeleteCert(cert.id)} className="text-red-300 hover:text-red-500 transition-colors shrink-0 p-1" data-testid={`delete-cert-${cert.id}`}>
                          <Trash className="w-4 h-4" weight="bold" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Company Logo */}
      {profile.company_logo && (
        <div className="mt-10 bg-white border border-slate-200/60 rounded-3xl p-6 sm:p-8 shadow-sm flex items-center justify-center">
          <img src={profile.company_logo} alt="Company Logo" className="max-h-32 w-auto object-contain" data-testid="company-logo" />
        </div>
      )}

      {/* ===== CATALOGUE SECTION ===== */}
      <div className="mt-10">
        <CatalogueSection
          items={catalogue}
          isOwner={isOwner}
          token={token}
          userId={userId}
          onRefresh={fetchCatalogue}
          uploadImage={uploadImage}
          uploading={uploading}
        />
      </div>

      {/* ===== TESTIMONIALS SECTION ===== */}
      {profileTestimonials.length > 0 && (
        <div className="mt-10" data-testid="profile-testimonials-section">
          <h2 className="text-slate-900 font-bold text-xl tracking-tight flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-2xl bg-purple-500/10 flex items-center justify-center">
              <Quotes className="w-5 h-5 text-purple-500" weight="fill" />
            </div>
            Testimonials
            <span className="text-slate-400 text-sm font-normal ml-1">({profileTestimonials.length})</span>
          </h2>
          <div className="relative group/scroll">
            {profileTestimonials.length > 3 && (
              <>
                <button onClick={() => { const el = document.getElementById('testimonials-scroll'); el?.scrollBy({ left: -380, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 opacity-0 group-hover/scroll:opacity-100 transition-opacity" data-testid="testimonials-scroll-left">
                  <CaretLeft className="w-4 h-4" weight="bold" />
                </button>
                <button onClick={() => { const el = document.getElementById('testimonials-scroll'); el?.scrollBy({ left: 380, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 opacity-0 group-hover/scroll:opacity-100 transition-opacity" data-testid="testimonials-scroll-right">
                  <CaretRight className="w-4 h-4" weight="bold" />
                </button>
              </>
            )}
            <div id="testimonials-scroll" className="flex gap-5 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
              {profileTestimonials.map(t => (
                <div key={t.id} className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300 relative shrink-0 w-[calc(33.333%-14px)] min-w-[280px] flex flex-col" data-testid={`profile-testimonial-${t.id}`}>
                  <Quotes className="w-8 h-8 text-purple-200 absolute top-5 right-5" weight="fill" />
                  <p className="text-slate-600 text-sm leading-relaxed italic mb-5 relative z-10 flex-1 line-clamp-4">&ldquo;{t.testimonial_text}&rdquo;</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                      {t.from_user_name?.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-slate-800 font-semibold text-sm truncate">{t.from_user_name}</p>
                      <p className="text-slate-400 text-xs truncate">{t.from_business_name} &middot; {t.from_chapter_name}</p>
                    </div>
                    <span className="px-2.5 py-1 rounded-full bg-purple-50 text-purple-600 text-[10px] font-semibold uppercase tracking-wider shrink-0">
                      {t.service_taken}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== MEMBERSHIP STATUS — Owner only ===== */}
      {isOwner && membershipStatus && membershipStatus.status !== "no_expiry" && (
        <div className="mt-8" data-testid="membership-status-section">
          <div
            className={`rounded-3xl p-6 border flex items-center gap-4 ${
              membershipStatus.status === "expired"
                ? "bg-red-50 border-red-200"
                : membershipStatus.status === "expiring_soon"
                ? "bg-amber-50 border-amber-200"
                : "bg-emerald-50 border-emerald-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
              membershipStatus.status === "expired" ? "bg-red-100" :
              membershipStatus.status === "expiring_soon" ? "bg-amber-100" : "bg-emerald-100"
            }`}>
              <ShieldCheck
                className={`w-6 h-6 ${
                  membershipStatus.status === "expired" ? "text-red-500" :
                  membershipStatus.status === "expiring_soon" ? "text-amber-500" : "text-emerald-600"
                }`}
                weight="fill"
              />
            </div>
            <div className="flex-1">
              <p className="text-slate-800 font-semibold">
                {membershipStatus.status === "expired"
                  ? "Membership Expired"
                  : membershipStatus.status === "expiring_soon"
                  ? `Membership expiring in ${membershipStatus.days_remaining} days`
                  : `Membership Active — ${membershipStatus.days_remaining} days remaining`}
              </p>
              <div className="flex items-center gap-4 mt-1">
                {profile.created_at && (
                  <p className="text-slate-500 text-sm">
                    Joined: {new Date(profile.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                  </p>
                )}
                {membershipStatus.expiry_date && (
                  <p className="text-slate-500 text-sm">
                    Expires: {new Date(membershipStatus.expiry_date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isLoggedIn) return <DashboardLayout>{profileContent}</DashboardLayout>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="public-profile-standalone">
      {/* Glassmorphic Header */}
      <div className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="BCH" className="h-24 w-auto object-contain" />
          <Link to="/login" className="text-slate-500 text-xs hover:text-slate-700 transition-colors duration-300">
            Member? <span className="text-[#2A9D8F] font-semibold">Sign in</span>
          </Link>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">{profileContent}</div>
    </div>
  );
}

/* ============== CATALOGUE SECTION ============== */
const CatalogueSection = ({ items, isOwner, token, userId, onRefresh, uploadImage, uploading }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ heading: "", sub_heading: "", price: "", description: "", image_url: "" });
  const imgRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) setForm(p => ({ ...p, image_url: url }));
  };

  const handleAdd = async () => {
    if (!form.heading.trim()) { toast.error("Heading is required"); return; }
    try {
      await axios.post(`${API}/catalogue`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Item added!");
      setForm({ heading: "", sub_heading: "", price: "", description: "", image_url: "" });
      setAdding(false);
      onRefresh();
    } catch { toast.error("Failed to add item"); }
  };

  const handleDelete = async (itemId) => {
    try {
      await axios.delete(`${API}/catalogue/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Item removed");
      onRefresh();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="catalogue-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-slate-900 font-bold text-xl tracking-tight flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
            <Package className="w-5 h-5 text-violet-500" weight="duotone" />
          </div>
          Products & Services
        </h2>
        {isOwner && !adding && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#2A9D8F]/8 text-[#1F7A6F] border border-[#2A9D8F]/15 rounded-xl text-xs font-semibold hover:-translate-y-0.5 hover:shadow-md hover:bg-[#2A9D8F]/12 transition-all duration-300"
            data-testid="add-catalogue-btn"
          >
            <Plus className="w-4 h-4" weight="bold" /> Add Item
          </button>
        )}
      </div>

      {/* Add Form */}
      {adding && (
        <div className="bg-white border border-slate-200/60 rounded-3xl p-6 sm:p-8 mb-8 shadow-sm" data-testid="catalogue-add-form">
          <h3 className="text-slate-900 font-bold text-base tracking-tight mb-5">New Product / Service</h3>
          {/* Image Upload */}
          <div className="mb-5">
            <p className="text-slate-400 text-[11px] uppercase tracking-widest font-bold mb-2">Product Image</p>
            <div className="flex items-center gap-4">
              <div
                className="w-24 h-24 rounded-2xl bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#2A9D8F]/40 transition-colors duration-300"
                onClick={() => imgRef.current?.click()}
              >
                {form.image_url ? (
                  <img src={form.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-7 h-7 text-slate-300" weight="duotone" />
                )}
              </div>
              <div>
                <button
                  onClick={() => imgRef.current?.click()}
                  className="text-[#2A9D8F] text-xs font-semibold hover:underline"
                  data-testid="catalogue-upload-img-btn"
                >
                  {uploading ? "Uploading..." : "Upload Image"}
                </button>
                <p className="text-slate-400 text-[10px] mt-1">JPEG, PNG, WebP under 5MB</p>
              </div>
              <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-slate-400 text-[11px] uppercase tracking-widest font-bold mb-2">Heading *</p>
              <Input value={form.heading} onChange={e => setForm(p => ({ ...p, heading: e.target.value }))} placeholder="Product or service name" className="bg-slate-50 border-slate-200 text-slate-900 focus:border-[#2A9D8F] rounded-xl" data-testid="catalogue-heading" />
            </div>
            <div>
              <p className="text-slate-400 text-[11px] uppercase tracking-widest font-bold mb-2">Price</p>
              <Input value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} placeholder="e.g. ₹5,000" className="bg-slate-50 border-slate-200 text-slate-900 focus:border-[#2A9D8F] rounded-xl font-mono" data-testid="catalogue-price" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-slate-400 text-[11px] uppercase tracking-widest font-bold mb-2">Sub Heading</p>
            <Input value={form.sub_heading} onChange={e => setForm(p => ({ ...p, sub_heading: e.target.value }))} placeholder="Brief tagline" className="bg-slate-50 border-slate-200 text-slate-900 focus:border-[#2A9D8F] rounded-xl" data-testid="catalogue-subheading" />
          </div>
          <div className="mt-4">
            <p className="text-slate-400 text-[11px] uppercase tracking-widest font-bold mb-2">Description</p>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="Short description of your product or service" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 text-sm focus:border-[#2A9D8F] focus:outline-none focus:ring-1 focus:ring-[#2A9D8F] resize-none transition-colors" data-testid="catalogue-description" />
          </div>
          <div className="flex gap-3 pt-5 border-t border-slate-100 mt-5">
            <Button onClick={handleAdd} size="sm" className="bg-[#2A9D8F] hover:bg-[#1F7A6F] text-white rounded-xl px-5 font-semibold" data-testid="catalogue-save-btn">
              <Check className="w-3.5 h-3.5 mr-1.5" weight="bold" /> Save Item
            </Button>
            <Button onClick={() => setAdding(false)} size="sm" variant="ghost" className="text-slate-500 rounded-xl">Cancel</Button>
          </div>
        </div>
      )}

      {/* Catalogue Grid — 4 per row, horizontal scroll */}
      {items.length > 0 ? (
        <div className="relative group/scroll">
          {items.length > 4 && (
            <>
              <button onClick={() => { const el = document.getElementById('catalogue-scroll'); el?.scrollBy({ left: -300, behavior: 'smooth' }); }}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 opacity-0 group-hover/scroll:opacity-100 transition-opacity" data-testid="catalogue-scroll-left">
                <CaretLeft className="w-4 h-4" weight="bold" />
              </button>
              <button onClick={() => { const el = document.getElementById('catalogue-scroll'); el?.scrollBy({ left: 300, behavior: 'smooth' }); }}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 opacity-0 group-hover/scroll:opacity-100 transition-opacity" data-testid="catalogue-scroll-right">
                <CaretRight className="w-4 h-4" weight="bold" />
              </button>
            </>
          )}
          <div id="catalogue-scroll" className="flex gap-5 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: "none" }} data-testid="catalogue-grid">
            {items.map((item, idx) => (
              <div
                key={item.id}
                className="group bg-white border border-slate-200/60 rounded-3xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 shrink-0 w-[calc(25%-15px)]"
                data-testid={`catalogue-item-${item.id}`}
                style={{ animationDelay: `${idx * 80}ms`, minWidth: "220px" }}
              >
              {item.image_url ? (
                <div className="h-48 bg-slate-50 overflow-hidden">
                  <img src={item.image_url} alt={item.heading} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              ) : (
                <div className="h-32 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                  <Package className="w-10 h-10 text-slate-200" weight="duotone" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-slate-900 font-bold text-sm leading-snug">{item.heading}</h3>
                  {isOwner && (
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="shrink-0 p-2 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all duration-200"
                      data-testid={`delete-catalogue-${item.id}`}
                    >
                      <Trash className="w-4 h-4" weight="bold" />
                    </button>
                  )}
                </div>
                {item.sub_heading && <p className="text-slate-400 text-xs mt-1.5">{item.sub_heading}</p>}
                {item.price && (
                  <p className="text-[#2A9D8F] font-bold text-base mt-3 font-mono tracking-tight flex items-center gap-1">
                    <CurrencyInr className="w-4 h-4" weight="duotone" />
                    {item.price}
                  </p>
                )}
                {item.description && <p className="text-slate-500 text-xs mt-3 leading-relaxed line-clamp-3">{item.description}</p>}
              </div>
            </div>
          ))}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200/60 rounded-3xl p-12 text-center shadow-sm">
          <div className="w-16 h-16 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-violet-300" weight="duotone" />
          </div>
          <p className="text-slate-400 text-sm font-medium">{isOwner ? "Add your products & services to showcase your business" : "No items listed yet"}</p>
        </div>
      )}
    </div>
  );
};

/* ============== HELPER COMPONENTS ============== */
const EditField = ({ label, value, onChange, multiline, testId, maxLength, inputMode }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <p className="text-slate-400 text-[11px] uppercase tracking-widest font-bold">{label}</p>
      {maxLength && <span className={`text-[11px] font-medium ${(value?.length || 0) >= maxLength ? "text-red-400" : "text-slate-300"}`}>{value?.length || 0}/{maxLength}</span>}
    </div>
    {multiline ? (
      <textarea
        value={value}
        onChange={e => onChange(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
        maxLength={maxLength}
        rows={3}
        data-testid={testId}
        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 text-sm focus:border-[#2A9D8F] focus:outline-none focus:ring-1 focus:ring-[#2A9D8F] resize-none transition-colors"
      />
    ) : (
      <Input
        value={value}
        onChange={e => onChange(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
        maxLength={maxLength}
        inputMode={inputMode}
        data-testid={testId}
        className="bg-slate-50 border-slate-200 text-slate-900 focus:border-[#2A9D8F] rounded-xl"
      />
    )}
  </div>
);

const DetailRow = ({ icon: Icon, label, value, isLink, isMono }) => (
  <div className="flex items-center gap-3.5">
    <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-slate-400" weight="duotone" />
    </div>
    <div className="min-w-0">
      <p className="text-slate-400 text-[10px] uppercase tracking-wider font-bold">{label}</p>
      {isLink ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-[#2A9D8F] text-sm font-medium hover:underline truncate block">{value}</a>
      ) : (
        <p className={`text-slate-700 text-sm font-medium truncate ${isMono ? "font-mono" : ""}`}>{value || "—"}</p>
      )}
    </div>
  </div>
);
